/**
 * Agent Session Provider - Clean UI State Manager
 *
 * Responsibilities:
 * - Provide adapter instance to hooks
 * - Track UI state only (currentSessionId, loading, errors)
 * - Handle adapter event listeners for UI updates
 * - NO session persistence (adapter owns this)
 *
 * TD-1904: agentState, isSending, searchMetadata, searchProgress are now
 * tracked per-session (keyed by sessionId) instead of as global scalars.
 * This allows multiple chats to operate independently.
 */

import { useReducer, useRef, useEffect, useMemo, useCallback } from 'react';
import AgentSessionContext from './AgentSessionContext';
import AgentCoreAdapterFactory from '../adapters/AgentCoreAdapterFactory';
import { useStore } from '../../../store/store';
import pubSub from '../../../utils/pubsub';
import {
  eventAgenticAiNewChat,
  eventAgenticAiMessageSent,
  eventAgenticAiResponseReceived
} from '../../../ga4/ga4';

// Per-session message-send timestamps (ms), used to compute AI response duration.
const agentResponseSendTimes = {};

// ============================================================================
// UI STATE MANAGEMENT (No Persistence)
// ============================================================================

const UI_ACTIONS = {
  SET_CURRENT_SESSION_ID: 'SET_CURRENT_SESSION_ID',
  SET_SESSION_AGENT_STATE: 'SET_SESSION_AGENT_STATE',
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
  SET_LOADING: 'SET_LOADING',
  SET_SESSION_SENDING: 'SET_SESSION_SENDING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  TRIGGER_MESSAGE_UPDATE: 'TRIGGER_MESSAGE_UPDATE',
  SET_SESSION_SEARCH_METADATA: 'SET_SESSION_SEARCH_METADATA',
  SET_SESSION_SEARCH_PROGRESS: 'SET_SESSION_SEARCH_PROGRESS',
  CLEAR_SESSION_STATE: 'CLEAR_SESSION_STATE',
  SET_ALL_SESSIONS: 'SET_ALL_SESSIONS',
  SET_LOADING_SESSIONS: 'SET_LOADING_SESSIONS',
  SET_SESSIONS_SETTLED: 'SET_SESSIONS_SETTLED'
};

/**
 * UI state only - no session data
 * TD-1904: Per-session state uses plain objects keyed by sessionId
 */
const createInitialState = () => ({
  currentSessionId: null,
  sessionAgentStates: {},
  connectionStatus: 'disconnected',
  isLoading: false,
  sessionSendingStates: {},
  error: null,
  messageUpdateTrigger: 0,
  sessionSearchMetadata: {},
  sessionSearchProgress: {},
  // TD-2013: Centralized session list state (fetched once by provider, shared by all hooks)
  allSessions: [],
  isLoadingSessions: true,
  // TD-2571: whether the backend session list has settled, and whether the backend was actually
  // reached. Distinct from isLoadingSessions, which a cache-first hit clears before the backend
  // has answered — treating that as settled would declare a thread missing while it is still
  // in flight.
  sessionsSettled: false,
  sessionsSettledOk: false
});

/**
 * UI state reducer - no persistence side effects
 */
const uiStateReducer = (state, action) => {
  if (import.meta.env.DEV) {
    console.log('🔄 UI Action:', action.type, action.payload);
  }

  switch (action.type) {
    case UI_ACTIONS.SET_CURRENT_SESSION_ID:
      return { ...state, currentSessionId: action.payload };

    case UI_ACTIONS.SET_SESSION_AGENT_STATE: {
      const { sessionId, agentState } = action.payload;
      return {
        ...state,
        sessionAgentStates: {
          ...state.sessionAgentStates,
          [sessionId]: agentState
        }
      };
    }

    case UI_ACTIONS.SET_CONNECTION_STATUS:
      return { ...state, connectionStatus: action.payload };

    case UI_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case UI_ACTIONS.SET_SESSION_SENDING: {
      const { sessionId, isSending } = action.payload;
      return {
        ...state,
        sessionSendingStates: {
          ...state.sessionSendingStates,
          [sessionId]: isSending
        }
      };
    }

    case UI_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };

    case UI_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    case UI_ACTIONS.TRIGGER_MESSAGE_UPDATE:
      return { ...state, messageUpdateTrigger: state.messageUpdateTrigger + 1 };

    case UI_ACTIONS.SET_SESSION_SEARCH_METADATA: {
      const { sessionId, searchMetadata } = action.payload;
      return {
        ...state,
        sessionSearchMetadata: {
          ...state.sessionSearchMetadata,
          [sessionId]: searchMetadata
        }
      };
    }

    case UI_ACTIONS.SET_SESSION_SEARCH_PROGRESS: {
      const { sessionId, searchProgress } = action.payload;
      return {
        ...state,
        sessionSearchProgress: {
          ...state.sessionSearchProgress,
          [sessionId]: searchProgress
        }
      };
    }

    case UI_ACTIONS.CLEAR_SESSION_STATE: {
      const { sessionId } = action.payload;
      const newAgentStates = { ...state.sessionAgentStates };
      const newSendingStates = { ...state.sessionSendingStates };
      const newSearchMetadata = { ...state.sessionSearchMetadata };
      const newSearchProgress = { ...state.sessionSearchProgress };
      delete newAgentStates[sessionId];
      delete newSendingStates[sessionId];
      delete newSearchMetadata[sessionId];
      delete newSearchProgress[sessionId];
      return {
        ...state,
        sessionAgentStates: newAgentStates,
        sessionSendingStates: newSendingStates,
        sessionSearchMetadata: newSearchMetadata,
        sessionSearchProgress: newSearchProgress
      };
    }

    case UI_ACTIONS.SET_ALL_SESSIONS:
      return { ...state, allSessions: action.payload };

    case UI_ACTIONS.SET_LOADING_SESSIONS:
      return { ...state, isLoadingSessions: action.payload };

    case UI_ACTIONS.SET_SESSIONS_SETTLED:
      return { ...state, sessionsSettled: true, sessionsSettledOk: action.payload };

    default:
      return state;
  }
};

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

// eslint-disable-next-line react/prop-types
const AgentSessionProvider = ({ children, adapterConfig }) => {
  const store = useStore();

  if (import.meta.env.DEV) {
    console.log('🏭 AgentSessionProvider: Clean UI state manager (adapter owns data)');
    console.log('🔧 Received adapter config:', {
      type: adapterConfig?.type,
      hasAgentCoreConfig: !!adapterConfig?.agentCoreConfig,
      hasMockConfig: !!adapterConfig?.mockConfig
    });
    console.log('🏪 AuthStore user info available:', {
      hasUserInfo: !!store.authStore.userInfo,
      currentUserId: store.authStore.currentUserId
    });
  }

  const [uiState, dispatch] = useReducer(uiStateReducer, null, createInitialState);

  const adapterRef = useRef(null);

  // TD-1911: Ref-based busy guard to prevent concurrent sends.
  // Using a ref (not state) so sendMessage can read it synchronously
  // without stale closure issues from useCallback([]).
  // TD-1904: Session-keyed object instead of boolean for multi-session support.
  const agentBusyRef = useRef({});

  if (!adapterRef.current) {
    if (!adapterConfig) {
      throw new Error('AgentSessionProvider requires adapterConfig prop');
    }

    if (import.meta.env.DEV) {
      console.log('🎯 Creating adapter with provided config and store (one time only)');
    }
    adapterRef.current = AgentCoreAdapterFactory.create(adapterConfig, store);
  }

  // ============================================================================
  // TD-2013: CENTRALIZED SESSION FETCHING (once per provider, shared by all hooks)
  // ============================================================================

  const fetchSessions = useCallback(async () => {
    try {
      dispatch({ type: UI_ACTIONS.SET_LOADING_SESSIONS, payload: true });
      const sessions = await adapterRef.current.getAllSessions();
      const sortedSessions = [...sessions].sort(
        (a, b) => (b.lastActivity || b.createdAt || 0) - (a.lastActivity || a.createdAt || 0)
      );
      dispatch({ type: UI_ACTIONS.SET_ALL_SESSIONS, payload: sortedSessions });
    } catch (error) {
      console.error('❌ Failed to fetch sessions:', error);
      dispatch({ type: UI_ACTIONS.SET_ERROR, payload: error.message });
    } finally {
      dispatch({ type: UI_ACTIONS.SET_LOADING_SESSIONS, payload: false });
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // ============================================================================
  // PER-SESSION STATE GETTERS (TD-1904)
  // Ref holds latest state so getters are stable across renders.
  // ============================================================================

  const uiStateRef = useRef(uiState);
  uiStateRef.current = uiState;

  const getSessionAgentState = useCallback((sessionId) => {
    return uiStateRef.current.sessionAgentStates[sessionId] || 'idle';
  }, []);

  const getSessionIsSending = useCallback((sessionId) => {
    return uiStateRef.current.sessionSendingStates[sessionId] || false;
  }, []);

  const getSessionSearchMetadata = useCallback((sessionId) => {
    return uiStateRef.current.sessionSearchMetadata[sessionId] || null;
  }, []);

  const getSessionSearchProgress = useCallback((sessionId) => {
    return uiStateRef.current.sessionSearchProgress[sessionId] || null;
  }, []);

  // ============================================================================
  // UI ACTIONS (Stable References)
  // ============================================================================

  const createSession = useCallback(async (initialMessage) => {
    if (import.meta.env.DEV) {
      console.log('🚀 Creating session with message:', initialMessage);
    }
    dispatch({ type: UI_ACTIONS.SET_LOADING, payload: true });

    try {
      const sessionIds = await adapterRef.current.createSession();
      if (import.meta.env.DEV) {
        console.log('✅ Adapter created session:', sessionIds);
      }

      dispatch({ type: UI_ACTIONS.SET_CURRENT_SESSION_ID, payload: sessionIds.sessionId });
      eventAgenticAiNewChat(sessionIds.sessionId);

      dispatch({ type: UI_ACTIONS.SET_CONNECTION_STATUS, payload: 'connecting' });
      await adapterRef.current.connect(sessionIds.sessionId);
      dispatch({ type: UI_ACTIONS.SET_CONNECTION_STATUS, payload: 'connected' });

      if (initialMessage) {
        dispatch({
          type: UI_ACTIONS.SET_SESSION_SENDING,
          payload: { sessionId: sessionIds.sessionId, isSending: true }
        });

        try {
          await adapterRef.current.sendMessage(sessionIds.sessionId, initialMessage);
          eventAgenticAiMessageSent(initialMessage?.length || 0, 'typed', sessionIds.sessionId);
          agentResponseSendTimes[sessionIds.sessionId] = Date.now();
          dispatch({
            type: UI_ACTIONS.SET_SESSION_SENDING,
            payload: { sessionId: sessionIds.sessionId, isSending: false }
          });
        } catch (messageError) {
          console.error('❌ Failed to send initial message:', messageError);
          dispatch({
            type: UI_ACTIONS.SET_SESSION_SENDING,
            payload: { sessionId: sessionIds.sessionId, isSending: false }
          });
        }
      }

      dispatch({ type: UI_ACTIONS.SET_LOADING, payload: false });

      const newSession = adapterRef.current.getSession(sessionIds.sessionId);
      // Prepend to allSessions so the sidebar reflects the new chat immediately
      // without a refetch. (The previous mount-effect in pages/Agent that papered
      // over this was removed alongside TD-2013's singleton fetch.)
      if (newSession) {
        dispatch({
          type: UI_ACTIONS.SET_ALL_SESSIONS,
          payload: [newSession, ...uiStateRef.current.allSessions]
        });
      }
      return newSession;
    } catch (error) {
      console.error('❌ Failed to create session:', error);
      dispatch({ type: UI_ACTIONS.SET_ERROR, payload: error.message });
      dispatch({ type: UI_ACTIONS.SET_LOADING, payload: false });
      throw error;
    }
  }, []);

  const loadSession = useCallback(async (sessionId) => {
    if (import.meta.env.DEV) {
      console.log('📂 Loading session:', sessionId);
    }
    dispatch({ type: UI_ACTIONS.SET_LOADING, payload: true });

    try {
      const session = adapterRef.current.getSession(sessionId);
      if (!session) {
        throw new Error(`Session ${sessionId} not found`);
      }

      // Setting the current session BEFORE the await is deliberate: it doubles as the in-flight
      // lock that stops the auto-load effect re-entering while connect() runs. The catch below
      // is what releases it.
      dispatch({ type: UI_ACTIONS.SET_CURRENT_SESSION_ID, payload: sessionId });
      dispatch({ type: UI_ACTIONS.SET_CONNECTION_STATUS, payload: 'connecting' });
      await adapterRef.current.connect(sessionId);

      dispatch({ type: UI_ACTIONS.SET_CONNECTION_STATUS, payload: 'connected' });
      dispatch({ type: UI_ACTIONS.SET_LOADING, payload: false });
    } catch (error) {
      console.error('❌ Failed to load session:', error);
      dispatch({ type: UI_ACTIONS.SET_ERROR, payload: error.message });
      dispatch({ type: UI_ACTIONS.SET_LOADING, payload: false });
      dispatch({ type: UI_ACTIONS.SET_CONNECTION_STATUS, payload: 'disconnected' });
      // TD-2571: release the in-flight lock. Leaving currentSessionId set meant the auto-load
      // effect's `currentSessionId !== sessionId` guard blocked every retry for the rest of the
      // page session, so one failed connect stuck the chat permanently.
      dispatch({ type: UI_ACTIONS.SET_CURRENT_SESSION_ID, payload: null });
      throw error;
    }
  }, []);

  const sendMessage = useCallback(async (content, targetSessionId = null, options = {}) => {
    const sessionId = targetSessionId || uiState.currentSessionId;
    if (!sessionId) {
      throw new Error('No active session');
    }

    // TD-1911: Reject sends while agent is busy (ref bypasses closure issues)
    // TD-1904: Session-keyed guard — needs sessionId so placed after resolution
    if (agentBusyRef.current[sessionId]) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ Cannot send message: agent is busy');
      }
      return;
    }

    if (import.meta.env.DEV) {
      console.log('💬 Sending message to session:', sessionId);
    }
    dispatch({
      type: UI_ACTIONS.SET_SESSION_SENDING,
      payload: { sessionId, isSending: true }
    });

    try {
      await adapterRef.current.sendMessage(sessionId, content, options);
      eventAgenticAiMessageSent(content?.length || 0, 'typed', sessionId);
      agentResponseSendTimes[sessionId] = Date.now();
      dispatch({
        type: UI_ACTIONS.SET_SESSION_SENDING,
        payload: { sessionId, isSending: false }
      });
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      dispatch({ type: UI_ACTIONS.SET_ERROR, payload: error.message });
      dispatch({
        type: UI_ACTIONS.SET_SESSION_SENDING,
        payload: { sessionId, isSending: false }
      });
      throw error;
    }
  }, []);

  const retryMessage = useCallback(async (sessionId, messageId) => {
    // TD-1911: Same busy guard as sendMessage (TD-1904: session-keyed)
    if (agentBusyRef.current[sessionId]) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ Cannot retry message: agent is busy');
      }
      return;
    }

    if (!adapterRef.current.retryMessage) {
      throw new Error('Retry functionality not supported by current adapter');
    }

    if (import.meta.env.DEV) {
      console.log('🔄 Retrying message:', messageId, 'in session:', sessionId);
    }
    dispatch({
      type: UI_ACTIONS.SET_SESSION_SENDING,
      payload: { sessionId, isSending: true }
    });

    try {
      await adapterRef.current.retryMessage(sessionId, messageId);
      dispatch({
        type: UI_ACTIONS.SET_SESSION_SENDING,
        payload: { sessionId, isSending: false }
      });
    } catch (error) {
      console.error('❌ Failed to retry message:', error);
      dispatch({ type: UI_ACTIONS.SET_ERROR, payload: error.message });
      dispatch({
        type: UI_ACTIONS.SET_SESSION_SENDING,
        payload: { sessionId, isSending: false }
      });
      throw error;
    }
  }, []);

  // TD-2293: hidden control reply (reveal confirm / cancel / poll). Unlike
  // sendMessage there is no busy-guard and no isSending flip — this is a
  // background turn on the user's behalf (like the search polls): the user-
  // side message never renders and the agent's answer arrives as a normal
  // visible message. Must target the session that holds the quote.
  const sendControlReply = useCallback(async (content, targetSessionId = null) => {
    const sessionId = targetSessionId || uiState.currentSessionId;
    if (!sessionId) {
      throw new Error('No active session');
    }
    if (typeof adapterRef.current.sendControlReply !== 'function') {
      throw new TypeError('Control replies not supported by current adapter');
    }
    try {
      await adapterRef.current.sendControlReply(sessionId, content);
    } catch (error) {
      console.error('❌ Failed to send control reply:', error);
      dispatch({ type: UI_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: UI_ACTIONS.CLEAR_ERROR });
  }, []);

  // TD-2347: stop the in-flight turn for a session — delegates to the adapter
  // (aborts the request, halts search polling, finalizes the message) and clears
  // the sending flag so the input bar is immediately usable again.
  const stopMessage = useCallback((targetSessionId = null) => {
    const sessionId = targetSessionId || uiState.currentSessionId;
    if (!sessionId) {
      return;
    }
    adapterRef.current.stopMessage(sessionId);
    dispatch({
      type: UI_ACTIONS.SET_SESSION_SENDING,
      payload: { sessionId, isSending: false }
    });
  }, []);

  // ============================================================================
  // DATA ACCESS METHODS (Provider wraps adapter)
  // ============================================================================

  const getSession = useCallback((sessionId) => {
    return adapterRef.current.getSession(sessionId);
  }, []);

  const getMessages = useCallback((sessionId) => {
    return adapterRef.current.getMessages(sessionId);
  }, []);

  const getRecentSessions = useCallback(async (limit = 10) => {
    return await adapterRef.current.getRecentSessions(limit);
  }, []);

  const updateSession = useCallback(async (sessionId, updates) => {
    if (import.meta.env.DEV) {
      console.log('✏️ Provider: Updating session:', sessionId, updates);
    }

    try {
      const updatedSession = await adapterRef.current.updateSession(sessionId, updates);
      dispatch({ type: UI_ACTIONS.TRIGGER_MESSAGE_UPDATE });
      return updatedSession;
    } catch (error) {
      console.error('❌ Provider: Failed to update session:', error);
      dispatch({ type: UI_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, []);

  const deleteSession = useCallback(async (sessionId) => {
    if (import.meta.env.DEV) {
      console.log('🗑️ Provider: Deleting session:', sessionId);
    }

    try {
      await adapterRef.current.deleteSession(sessionId);
      // TD-1904: Clean up per-session state
      delete agentBusyRef.current[sessionId];
      dispatch({ type: UI_ACTIONS.CLEAR_SESSION_STATE, payload: { sessionId } });
      dispatch({ type: UI_ACTIONS.TRIGGER_MESSAGE_UPDATE });
    } catch (error) {
      console.error('❌ Provider: Failed to delete session:', error);
      dispatch({ type: UI_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, []);

  const touchSession = useCallback((sessionId) => {
    if (import.meta.env.DEV) {
      console.log('👆 Provider: Touching session:', sessionId);
    }
    const result = adapterRef.current.touchSession(sessionId);
    if (result) {
      dispatch({ type: UI_ACTIONS.TRIGGER_MESSAGE_UPDATE });
    }
    return result;
  }, []);

  const searchSessions = useCallback(async (query) => {
    if (import.meta.env.DEV) {
      console.log('🔍 Provider: Searching sessions:', query);
    }

    try {
      const results = await adapterRef.current.searchSessions(query);
      return results;
    } catch (error) {
      console.error('❌ Provider: Search failed:', error);
      throw error;
    }
  }, []);

  // ============================================================================
  // ADAPTER EVENT LISTENERS
  // ============================================================================

  const messageUpdateTrackingRef = useRef({
    lastHiddenUpdateTime: 0,
    pendingHiddenUpdate: null,
    updateCountInWindow: 0,
    windowStartTime: 0
  });

  useEffect(() => {
    const adapter = adapterRef.current;

    // TD-1904: State change events now include sessionId
    const handleStateChange = (event) => {
      const { sessionId, state: newState } = event;

      if (import.meta.env.DEV) {
        console.log('🔄 Agent state change:', sessionId, newState);
      }
      // Agent finished responding (busy -> idle): emit response_received once.
      if (newState === 'idle' && agentBusyRef.current[sessionId]) {
        const sentAt = agentResponseSendTimes[sessionId];
        eventAgenticAiResponseReceived(sessionId, sentAt ? Date.now() - sentAt : null);
        delete agentResponseSendTimes[sessionId];
      }
      // TD-1911: Update busy ref BEFORE dispatch so sendMessage guard
      // always reflects the latest agent state without closure issues.
      // TD-1904: Session-keyed guard
      const wasBusy = agentBusyRef.current[sessionId] === true;
      agentBusyRef.current[sessionId] = newState !== 'idle';
      dispatch({
        type: UI_ACTIONS.SET_SESSION_AGENT_STATE,
        payload: { sessionId, agentState: newState }
      });

      // TD-2002: Publish state change for AgentTokenDisplay to refresh token usage
      pubSub.publish({ event: 'agent-state-change', sessionId, state: newState });

      // TD-2376: an agent turn just finished (busy → idle). The BE may have
      // consumed one or more demo search credits during it (each MCP search
      // tool hits a profiler endpoint that atomically increments the count).
      // Reconcile the demo counter from the BE so it updates live instead of
      // only on F5 — same refetch-from-BE approach AgentTokenDisplay uses for
      // Company Tokens (TD-2002). Demo-gated to avoid needless GET_USER_INFO.
      if (wasBusy && newState === 'idle' && store.profilerAccessStore?.isProfilerDemo) {
        store.authStore.refreshProfilerDemoCounter();
      }

      // Capture search metadata when entering searching state (session-scoped — TD-2952)
      const sessionSearchState =
        adapter.getSearchStateForSession?.(sessionId) ?? adapter.currentSearchState;
      const sessionHasActiveSearch =
        typeof adapter.isSearchActiveForSession === 'function'
          ? adapter.isSearchActiveForSession(sessionId)
          : !!sessionSearchState?.active;

      if (newState === 'searching' && sessionSearchState?.active) {
        const searchMetadata = {
          toolName: sessionSearchState.toolName,
          searchId: sessionSearchState.searchId,
          startTime: sessionSearchState.startTime
        };
        dispatch({
          type: UI_ACTIONS.SET_SESSION_SEARCH_METADATA,
          payload: { sessionId, searchMetadata }
        });

        if (import.meta.env.DEV) {
          console.log('🔍 Search metadata captured for session:', sessionId, searchMetadata);
        }
      } else if (newState !== 'searching' && !sessionHasActiveSearch) {
        dispatch({
          type: UI_ACTIONS.SET_SESSION_SEARCH_METADATA,
          payload: { sessionId, searchMetadata: null }
        });
        if (import.meta.env.DEV) {
          console.log('🔍 Search metadata cleared for session:', sessionId);
        }
      } else if (import.meta.env.DEV && newState !== 'searching' && sessionHasActiveSearch) {
        console.log('🔍 Preserving search metadata during polling (search still active)');
      }
    };

    const handleMessage = (event) => {
      if (import.meta.env.DEV) {
        console.log('📨 Message event (UI notification only):', event);
      }

      const tracking = messageUpdateTrackingRef.current;
      const now = Date.now();

      if (event.hidden) {
        if (tracking.pendingHiddenUpdate) {
          clearTimeout(tracking.pendingHiddenUpdate);
        }

        tracking.pendingHiddenUpdate = setTimeout(() => {
          tracking.pendingHiddenUpdate = null;
          tracking.lastHiddenUpdateTime = Date.now();
          dispatch({ type: UI_ACTIONS.TRIGGER_MESSAGE_UPDATE });
        }, 100);
        return;
      }

      if (now - tracking.windowStartTime > 1000) {
        tracking.updateCountInWindow = 0;
        tracking.windowStartTime = now;
      }

      tracking.updateCountInWindow++;

      if (tracking.updateCountInWindow > 10) {
        if (import.meta.env.DEV) {
          console.error(
            '🔴 AgentSessionProvider: Circuit breaker triggered - too many visible message updates (>10/sec). Possible infinite loop.'
          );
        }
        return;
      }

      dispatch({ type: UI_ACTIONS.TRIGGER_MESSAGE_UPDATE });
    };

    // TD-1904: Search progress events now include sessionId
    const handleSearchProgress = (event) => {
      const { sessionId, ...progressData } = event;
      dispatch({
        type: UI_ACTIONS.SET_SESSION_SEARCH_PROGRESS,
        payload: { sessionId, searchProgress: progressData }
      });
    };

    // TD-2571: the background refresh repopulates the adapter's session map without telling React
    // anything, so a thread that only arrives on that refresh was never picked up. Republish it as
    // state, and record that the backend has answered so consumers can stop guessing.
    const handleSessionsRefreshed = ({ sessions, ok }) => {
      const sortedSessions = [...(sessions || [])].sort(
        (a, b) => (b.lastActivity || b.createdAt || 0) - (a.lastActivity || a.createdAt || 0)
      );
      dispatch({ type: UI_ACTIONS.SET_ALL_SESSIONS, payload: sortedSessions });
      dispatch({ type: UI_ACTIONS.TRIGGER_MESSAGE_UPDATE });
      dispatch({ type: UI_ACTIONS.SET_SESSIONS_SETTLED, payload: ok });
    };

    const unsubStateChange = adapter.onStateChange(handleStateChange);
    const unsubMessage = adapter.onMessage(handleMessage);
    const unsubSearchProgress = adapter.onSearchProgress(handleSearchProgress);
    // Guarded: an adapter predating this contract simply never settles rather than crashing.
    const unsubSessionsRefreshed =
      typeof adapter.onSessionsRefreshed === 'function'
        ? adapter.onSessionsRefreshed(handleSessionsRefreshed)
        : null;

    return () => {
      if (typeof unsubStateChange === 'function') unsubStateChange();
      if (typeof unsubMessage === 'function') unsubMessage();
      if (typeof unsubSearchProgress === 'function') unsubSearchProgress();
      if (typeof unsubSessionsRefreshed === 'function') unsubSessionsRefreshed();
      if (messageUpdateTrackingRef.current.pendingHiddenUpdate) {
        clearTimeout(messageUpdateTrackingRef.current.pendingHiddenUpdate);
      }
    };
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const contextValue = useMemo(() => {
    return {
      // UI State
      currentSessionId: uiState.currentSessionId,
      connectionStatus: uiState.connectionStatus,
      isLoading: uiState.isLoading,
      error: uiState.error,
      messageUpdateTrigger: uiState.messageUpdateTrigger,

      // TD-1904: Per-session state getters
      getSessionAgentState,
      getSessionIsSending,
      getSessionSearchMetadata,
      getSessionSearchProgress,

      // UI Actions (stable references)
      createSession,
      loadSession,
      sendMessage,
      retryMessage,
      stopMessage,
      sendControlReply,
      clearError,

      // Data Access Methods (stable references)
      getSession,
      getMessages,
      // getAllSessions intentionally NOT exposed on context — would let callers bypass the singleton fetch pattern.
      // Use context.allSessions (centralized) or context.refreshSessions() instead.
      getRecentSessions,

      // Session Management Methods
      updateSession,
      deleteSession,
      touchSession,
      searchSessions,

      // TD-2013: Centralized session list (fetched once, shared by all hooks)
      allSessions: uiState.allSessions,
      isLoadingSessions: uiState.isLoadingSessions,
      refreshSessions: fetchSessions,

      // TD-2571: has the backend list answered, and did it get through?
      sessionsSettled: uiState.sessionsSettled,
      sessionsSettledOk: uiState.sessionsSettledOk
    };
  }, [
    uiState.currentSessionId,
    uiState.connectionStatus,
    uiState.isLoading,
    uiState.error,
    uiState.messageUpdateTrigger,
    // TD-1904: Session state maps trigger context update so consumers re-render
    uiState.sessionAgentStates,
    uiState.sessionSendingStates,
    uiState.sessionSearchMetadata,
    uiState.sessionSearchProgress,
    // TD-2013: Session list state
    uiState.allSessions,
    uiState.isLoadingSessions,
    uiState.sessionsSettled,
    uiState.sessionsSettledOk,
    // Stable refs (from useCallback with [] deps) — listed for exhaustive-deps lint
    getSessionAgentState,
    getSessionIsSending,
    getSessionSearchMetadata,
    getSessionSearchProgress,
    createSession,
    loadSession,
    sendMessage,
    retryMessage,
    stopMessage,
    sendControlReply,
    clearError,
    getSession,
    getMessages,
    getRecentSessions,
    updateSession,
    deleteSession,
    fetchSessions,
    touchSession,
    searchSessions
  ]);

  return (
    <AgentSessionContext.Provider value={contextValue}>{children}</AgentSessionContext.Provider>
  );
};

export default AgentSessionProvider;
